import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTutorial } from "@/contexts/TutorialContext";
import { TutorialSlide } from "./TutorialSlide";
import { TutorialProgressDots } from "./TutorialProgressDots";
import { cn } from "@/lib/utils";

export const TutorialModal = () => {
  const { currentTutorial, isOpen, closeTutorial, markComplete } = useTutorial();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Reset slide when tutorial changes
  useEffect(() => {
    if (currentTutorial) {
      setCurrentSlide(0);
    }
  }, [currentTutorial?.id]);

  if (!currentTutorial) return null;

  const slides = currentTutorial.slides;
  const isLastSlide = currentSlide === slides.length - 1;
  const isFirstSlide = currentSlide === 0;

  const handleNext = () => {
    if (isLastSlide) {
      handleComplete();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstSlide) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    await markComplete(currentTutorial.id);
    closeTutorial();
  };

  const handleSkip = async () => {
    await markComplete(currentTutorial.id);
    closeTutorial();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent
        className={cn(
          "bg-primary border-primary-muted p-0 gap-0 overflow-hidden",
          "w-full max-w-lg mx-auto",
          "sm:rounded-2xl",
          "max-h-[90vh]"
        )}
        aria-describedby={undefined}
      >
        {/* Accessibility: Hidden title and description for screen readers */}
        <VisuallyHidden>
          <DialogTitle>{currentTutorial.title}</DialogTitle>
        </VisuallyHidden>

        {/* Header with skip button */}
        <div className="flex items-center justify-between p-4 border-b border-primary-muted">
          <div className="flex items-center gap-2">
            <currentTutorial.icon className="w-5 h-5 text-accent" />
            <span className="text-primary-foreground font-medium">{currentTutorial.title}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Slides container */}
        <div className="relative min-h-[350px] flex items-center justify-center">
          {slides.map((slide, index) => (
            <TutorialSlide
              key={index}
              slide={slide}
              isActive={index === currentSlide}
            />
          ))}
        </div>

        {/* Footer with navigation */}
        <div className="p-6 border-t border-primary-muted space-y-4">
          {/* Progress dots */}
          <TutorialProgressDots
            total={slides.length}
            current={currentSlide}
            onDotClick={setCurrentSlide}
          />

          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={isFirstSlide}
              className={cn(
                "text-primary-foreground hover:bg-white/10",
                isFirstSlide && "opacity-0 pointer-events-none"
              )}
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Précédent
            </Button>

            <Button
              onClick={handleNext}
              className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-8"
            >
              {isLastSlide ? (
                "Terminer"
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
